export type CreateSimpleCacheOptions<TCacheState extends Record<string, any>> = {
    defaultTtl?: number; // default infinity
}

export type SimpleCache<TCacheState extends Record<string, any>> = TCacheState & {
    isStale(key: keyof TCacheState): boolean;
    isFresh(key: keyof TCacheState): boolean;
}

export function createSimpleCache<TCacheState extends Record<string, any>>(
    initialState: TCacheState,
    opts?: CreateSimpleCacheOptions<TCacheState>): SimpleCache<TCacheState> {
    const timeouts = new Map<string, ReturnType<typeof setTimeout>>();
    const stale = new Map<keyof TCacheState, boolean>();
    const loaded = new Set<keyof TCacheState>();

    const isStale = (key: keyof TCacheState) => stale.has(key);
    const isFresh = (key: keyof TCacheState) => loaded.has(key) && !stale.has(key);

    const defaultTtl = opts?.defaultTtl || Infinity;

    return new Proxy({ ...initialState, isStale, isFresh }, {
        set(target, prop, value) {
            const propString = String(prop);
            //console.log(`[Cache] set ${propString} = ${value}`);
            //console.log(`[Cache] starting timeout for "${propString}"`);

            if(loaded.has(propString)) {
                //console.log(`[Cache] already loaded "${propString}"`);
            } else {
                loaded.add(propString);
            }

            if(timeouts.has(propString)) {
                clearTimeout(timeouts.get(propString)!);
            }

            if(stale.has(propString)) {
                stale.delete(propString);
            }

            if(defaultTtl <= 0 || defaultTtl === Infinity) {
                return Reflect.set(target, prop, value);
            } else {
                timeouts.set(propString, setTimeout(() => {
                    // console.log(`[Cache] marking "${propString}" stale`);
                    stale.set(propString, true);
                    timeouts.delete(propString);
                }, defaultTtl));
                (target as any)[prop] = value;
                return true;
            }
        }
    })
}
